<template>
  <VBusHelper
    :reactOn="$constants.EVENTS.TRANSACTIONS.DETAIL.SHOW"
    :handler="() => $refs.modal.show()"
  />

  <DialogComponent
    ref="modal"
    type="bottomsheet"
    noInnerPadding
    hideCloseButton
  >
    <!-- Header -->
    <div class="absolute flex w-full">
      <ColorImg
        src="/images/leaderboard/cardHeadForm.svg"
        class="bg-indigo-500 w-full absolute -bottom-5"
        alt=""
      />
    </div>
    <div class="bg-indigo-500 relative mt-1">
      <div class="relative px-6 pt-0 pb-12">
        <!-- Back button -->
        <button
          @click="$refs.modal.hide()"
          class="w-10 h-10 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors mb-4"
        >
          <svg
            class="w-5 h-5 text-white"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M15 19l-7-7 7-7"
            />
          </svg>
        </button>

        <!-- Transaction info in header -->
        <div
          class="flex flex-col items-center gap-4"
          :class="{ shimmerContainer: loading }"
        >
          <!-- Logo with status badge -->
          <div class="relative">
            <div
              class="w-24 h-24 rounded-3xl bg-white/20 backdrop-blur-sm p-3 shimmerTarget flex items-center justify-center border-2 border-white/30"
            >
              <ImgComponent
                :src="transaction?.logo"
                alt=""
                class="w-full h-full object-contain"
              />
            </div>
            <div
              class="absolute -bottom-2 -right-2 w-10 h-10 rounded-xl flex items-center justify-center shadow-lg border-2 border-white/30"
              :class="{
                'bg-emerald-500': transaction?.operation == 'fund',
                'bg-rose-500': transaction?.operation == 'withdraw'
              }"
            >
              <ColorImg
                src="/images/icons/arrow-up.svg"
                alt=""
                class="w-5 h-5 bg-white"
                :class="{
                  'rotate-30': transaction?.operation == 'fund',
                  'rotate-210': transaction?.operation == 'withdraw'
                }"
              />
            </div>
          </div>

          <!-- Operation name -->
          <div class="text-center">
            <div class="font-bold text-2xl text-white shimmerTarget">
              {{ transaction?.operationNameDisplay || 'Chargement...' }}
            </div>
            <div class="text-white/70 text-sm mt-1 shimmerTarget">
              {{ transaction?.service || '---' }}
            </div>
          </div>

          <!-- Amount -->
          <div class="text-5xl font-bold text-white shimmerTarget">
            {{ transaction?.operation == 'fund' ? '+' : '-' }}
            {{ formatAmount(transaction?.amountToReceive) || '---' }} CFA
          </div>
        </div>
      </div>

      <div class="scale-x-[-1]">
        <svg
          class="absolute bottom-0 left-0 right-0"
          viewBox="0 0 1440 60"
          preserveAspectRatio="none"
          style="height: 28px"
        >
          <path
            d="M0 60V0C240 35 480 55 720 55S1200 35 1440 0V60H0Z"
            fill="white"
          />
        </svg>
      </div>
    </div>

    <!-- Content -->
    <div class="px-5 pt-12 pb-6 bg-white -mt-8">
      <!-- Details List -->

      <div class="flex flex-col gap-4" :class="{ shimmerContainer: loading }">

        <!-- Client info -->
        <div class="flex flex-col gap-4 text-sm py-2">
          <div class="flex justify-between">
            <div class="text-gray-600">Client</div>
            <div class="capitalize font-bold text-gray-900">
               {{ $stores.user?.state?.current?.firstName }}
               {{ $stores.user?.state?.current?.lastName }}
            </div>
          </div>

          <div class="flex justify-between">
            <div class="text-gray-600">Email</div>
            <div class="font-bold text-gray-900">
              {{ $stores.user?.state?.current?.email }}
            </div>
          </div>

          <div class="flex justify-between">
            <div class="text-gray-600">Pays</div>
            <div class="capitalize font-bold text-gray-900">
               {{ Countries.find($stores.user?.state?.current?.countryCode)?.countryName }}
            </div>
          </div>
          <div class="h-px bg-slate-100 my-1"></div>
        </div>

        <!-- Reference -->
        <div
          @click="!loading && copyReference()"
          class="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-2xl p-4 border border-indigo-100 cursor-pointer hover:shadow-md transition-all duration-200 group"
          :class="{ 'pointer-events-none': loading }"
        >
          <div class="flex items-center justify-between">
            <div class="flex items-center gap-3">
              <div
                class="w-10 h-10 rounded-xl bg-indigo-500 flex items-center justify-center"
              >
                <svg
                  class="w-5 h-5 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4"
                  />
                </svg>
              </div>
              <div>
                <div class="text-xs text-gray-500 uppercase tracking-wide">
                  Référence
                </div>
                <div
                  class="font-mono text-sm font-semibold text-gray-900 mt-0.5 truncate max-w-[200px] shimmerTarget"
                >
                  {{ transaction?.id || '---' }}
                </div>
              </div>
            </div>
            <div
              class="w-10 h-10 rounded-xl bg-white border border-indigo-200 flex items-center justify-center group-hover:bg-indigo-50 transition-colors"
            >
              <svg
                class="w-5 h-5 text-indigo-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                />
              </svg>
            </div>
          </div>
        </div>

        <!-- Status Cards -->
        <div class="grid gap-3" :class="showDepositStatus ? 'grid-cols-2' : 'grid-cols-1'">
          <div 
            class="rounded-2xl p-4 transition-colors duration-300"
            :class="{
              'bg-emerald-50 border border-emerald-100': transaction?.collectStatusGroup === 'success',
              'bg-amber-50 border border-amber-100': transaction?.collectStatusGroup === 'pending',
              'bg-rose-50 border border-rose-100': transaction?.collectStatusGroup === 'error',
              'bg-slate-50 border border-slate-100': transaction?.collectStatusGroup === 'unknown'
            }"
          >
            <div class="text-[10px] uppercase tracking-wider mb-2 shimmerTarget font-bold"
              :class="{
                'text-emerald-600': transaction?.collectStatusGroup === 'success',
                'text-amber-600': transaction?.collectStatusGroup === 'pending',
                'text-rose-600': transaction?.collectStatusGroup === 'error',
                'text-gray-500': transaction?.collectStatusGroup === 'unknown'
              }"
            >
              {{ transaction?.operationDetails?.collectName || '---' }}
            </div>
            <div class="flex items-center gap-2 font-bold text-gray-900 shimmerTarget">
              <!-- Icon -->
              <svg v-if="transaction?.collectStatusGroup === 'success'" class="w-4 h-4 text-emerald-500" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
              </svg>
              <svg v-else-if="transaction?.collectStatusGroup === 'pending'" class="w-4 h-4 text-amber-500 animate-pulse" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clip-rule="evenodd" />
              </svg>
              <svg v-else-if="transaction?.collectStatusGroup === 'error'" class="w-4 h-4 text-rose-500" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
              </svg>
              {{ transaction?.collectStatusDisplay || '---' }}
            </div>
          </div>
          <div 
            v-if="showDepositStatus"
            class="rounded-2xl p-4 transition-colors duration-300"
            :class="{
              'bg-emerald-50 border border-emerald-100': transaction?.depositStatusGroup === 'success',
              'bg-amber-50 border border-amber-100': transaction?.depositStatusGroup === 'pending',
              'bg-rose-50 border border-rose-100': transaction?.depositStatusGroup === 'error',
              'bg-slate-50 border border-slate-100': transaction?.depositStatusGroup === 'unknown'
            }"
          >
            <div class="text-[10px] uppercase tracking-wider mb-2 shimmerTarget font-bold"
              :class="{
                'text-emerald-600': transaction?.depositStatusGroup === 'success',
                'text-amber-600': transaction?.depositStatusGroup === 'pending',
                'text-rose-600': transaction?.depositStatusGroup === 'error',
                'text-gray-500': transaction?.depositStatusGroup === 'unknown'
              }"
            >
              {{ transaction?.operationDetails?.depositName || '---' }}
            </div>
            <div class="flex items-center gap-2 font-bold text-gray-900 shimmerTarget">
              <!-- Icon -->
              <svg v-if="transaction?.depositStatusGroup === 'success'" class="w-4 h-4 text-emerald-500" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
              </svg>
              <svg v-else-if="transaction?.depositStatusGroup === 'pending'" class="w-4 h-4 text-amber-500 animate-pulse" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clip-rule="evenodd" />
              </svg>
              <svg v-else-if="transaction?.depositStatusGroup === 'error'" class="w-4 h-4 text-rose-500" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
              </svg>
              {{ transaction?.depositStatusDisplay || '---' }}
            </div>
          </div>
        </div>

        <!-- Failure reason -->
        <div
          v-if="
            (transaction?.message || transaction?.reason)
          "
          class="bg-rose-50 rounded-2xl p-4 border border-rose-100 flex items-start gap-3"
        >
          <div
            class="w-10 h-10 rounded-xl bg-rose-500 flex items-center justify-center flex-shrink-0"
          >
            <svg
              class="w-5 h-5 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
          <div class="flex-1">
            <div class="font-semibold text-rose-900">Note</div>
            <div class="text-sm text-rose-700 leading-relaxed mt-0.5">
              {{ transaction?.message || transaction?.reason }}
            </div>
          </div>
        </div>

        <div class="border-t border-slate-200 my-2"></div>

        <!-- Amounts -->
        <div
          class="bg-white rounded-2xl border border-slate-200 divide-y divide-slate-100"
        >
          <div class="flex justify-between items-center p-4">
            <span class="text-gray-600 shimmerTarget">Montant envoyé</span>
            <div class="text-right">
              <span class="font-bold text-gray-900 shimmerTarget">
                {{ formatAmount(transaction?.amountToSend) || '---' }} CFA
              </span>
              <span
                v-if="getUsdtAmount('src')"
                class="block text-sm text-gray-500 shimmerTarget"
              >
                {{ getUsdtAmount('src') }} USDT
              </span>
            </div>
          </div>
          <div class="flex justify-between items-center p-4">
            <span class="text-gray-600 shimmerTarget">Montant reçu</span>
            <div class="text-right">
              <span class="font-bold text-gray-900 shimmerTarget">
                {{ formatAmount(transaction?.amountToReceive) || '---' }} CFA
              </span>
              <span
                v-if="getUsdtAmount('dst')"
                class="block text-sm text-gray-500 shimmerTarget"
              >
                {{ getUsdtAmount('dst') }} USDT
              </span>
            </div>
          </div>
        </div>

        <!-- Fees Breakdown -->
        <template v-if="hasFees">
          <div class="bg-amber-50 rounded-2xl p-4 border border-amber-100">
            <div class="flex items-center gap-2 mb-3">
              <svg
                class="w-5 h-5 text-amber-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <span class="font-semibold text-amber-900">Détail des frais</span>
            </div>
            <div class="space-y-2 text-sm">
              <div
                v-if="transaction?.feeBreakdown?.srcServiceFee"
                class="flex justify-between"
              >
                <span class="text-amber-700">Service source</span>
                <span class="font-semibold text-amber-900">
                  {{
                    formatAmount(transaction?.feeBreakdown?.srcServiceFee)
                  }}
                  CFA
                </span>
              </div>
              <div
                v-if="transaction?.feeBreakdown?.dstServiceFee"
                class="flex justify-between"
              >
                <span class="text-amber-700">Service destination</span>
                <span class="font-semibold text-amber-900">
                  {{
                    formatAmount(transaction?.feeBreakdown?.dstServiceFee)
                  }}
                  CFA
                </span>
              </div>
              <div
                v-if="transaction?.feeBreakdown?.srcProcessorFee"
                class="flex justify-between"
              >
                <span class="text-amber-700">Processeur source</span>
                <span class="font-semibold text-amber-900">
                  {{
                    formatAmount(transaction?.feeBreakdown?.srcProcessorFee)
                  }}
                  CFA
                </span>
              </div>
              <div
                v-if="transaction?.feeBreakdown?.dstProcessorFee"
                class="flex justify-between"
              >
                <span class="text-amber-700">Processeur destination</span>
                <span class="font-semibold text-amber-900">
                  {{
                    formatAmount(transaction?.feeBreakdown?.dstProcessorFee)
                  }}
                  CFA
                </span>
              </div>
              <div
                class="border-t border-amber-200 pt-2 mt-2 flex justify-between"
              >
                <span class="font-semibold text-amber-900">Total frais</span>
                <span class="font-bold text-rose-600">
                  {{ formatAmount(transaction?.fee) }} CFA
                </span>
              </div>
            </div>
          </div>
        </template>

        <!-- Bonus -->
        <div
          v-if="transaction?.bonus"
          class="bg-emerald-50 rounded-2xl p-4 border border-emerald-100 flex items-center gap-3"
        >
          <div
            class="w-10 h-10 rounded-xl bg-emerald-500 flex items-center justify-center flex-shrink-0"
          >
            <svg
              class="w-5 h-5 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7"
              />
            </svg>
          </div>
          <div class="flex-1">
            <div class="font-semibold text-emerald-900">Bonus reçu</div>
            <div class="text-2xl font-bold text-emerald-600">
              +{{ formatAmount(transaction?.bonus) }} CFA
            </div>
          </div>
        </div>

        <div class="border-t border-slate-200 my-2"></div>

        <!-- Additional Info -->
        <div
          class="bg-white rounded-2xl border border-slate-200 divide-y divide-slate-100"
        >
          <!-- Date -->
          <div class="flex justify-between items-center p-4">
            <div class="flex items-center gap-3">
              <div
                class="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center"
              >
                <svg
                  class="w-5 h-5 text-slate-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
              </div>
              <span class="text-gray-600 shimmerTarget">Date & heure</span>
            </div>
            <span class="font-semibold text-gray-900 shimmerTarget">{{
              transaction?.startDate || '---'
            }}</span>
          </div>

          <!-- Bookmaker ID -->
          <div
            v-if="transaction?.bookmakerId"
            class="flex justify-between items-center p-4"
          >
            <div class="flex items-center gap-3">
              <div
                class="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center"
              >
                <svg
                  class="w-5 h-5 text-slate-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14"
                  />
                </svg>
              </div>
              <span class="text-gray-600 shimmerTarget">Id {{ transaction?.service }}</span>
            </div>
            <span class="font-semibold text-gray-900 shimmerTarget">{{
              transaction?.bookmakerId
            }}</span>
          </div>

          <!-- Phone numbers -->
          <div
            v-if="
              transaction?.rawData?.src?.phoneNumber ||
              transaction?.senderPhoneNumber
            "
            class="flex justify-between items-center p-4"
          >
            <div class="flex items-center gap-3">
              <div
                class="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center"
              >
                <svg
                  class="w-5 h-5 text-slate-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                  />
                </svg>
              </div>
              <span class="text-gray-600 shimmerTarget">Téléphone</span>
            </div>
            <span class="font-semibold text-gray-900 shimmerTarget">
              {{
                transaction?.rawData?.src?.phoneNumber ||
                transaction?.senderPhoneNumber || '---'
              }}
            </span>
          </div>

          <!-- Destinataire -->
          <div
            v-if="
              transaction?.rawData?.dst?.phoneNumber ||
              transaction?.recipientPhoneNumber
            "
            class="flex justify-between items-center p-4"
          >
            <div class="flex items-center gap-3">
              <div
                class="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center"
              >
                <svg
                  class="w-5 h-5 text-slate-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                </svg>
              </div>
              <span class="text-gray-600 shimmerTarget">Destinataire</span>
            </div>
            <span class="font-semibold text-gray-900 shimmerTarget">
              {{
                transaction?.rawData?.dst?.phoneNumber ||
                transaction?.recipientPhoneNumber ||
                '---'
              }}
            </span>
          </div>

          <!-- Withdrawal Code -->
          <div
            v-if="transaction?.withdrawalCode"
            class="flex justify-between items-center p-4 bg-amber-50"
          >
            <div class="flex items-center gap-3">
              <div
                class="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center"
              >
                <svg
                  class="w-5 h-5 text-amber-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                  />
                </svg>
              </div>
              <span class="text-amber-900 font-bold">Code de retrait</span>
            </div>
            <span class="font-mono font-bold text-amber-900 text-lg">{{
              transaction?.withdrawalCode
            }}</span>
          </div>
        </div>

      </div>
    </div>
  </DialogComponent>
</template>

<script>
import Commons from '@/app/features/shared/libraries/commons';
import { Countries } from '@/app/features/shared/libraries/countries';

export default {
  name: 'TransactionDetailModal',

  setup() {
    return { Commons, Countries };
  },

  computed: {
    transaction() {
      return this.$stores.transactions?.state?.detail?.data;
    },
    loading() {
      return this.$stores.transactions?.state?.detail.loading;
    },
    showDepositStatus() {
      if (this.transaction?.status === 'failed' && this.transaction?.depositStatus === 'unstarted') {
        return false;
      }
      return true;
    },
    hasFees() {
      const fb = this.transaction?.feeBreakdown;
      return (
        fb?.srcServiceFee ||
        fb?.dstServiceFee ||
        fb?.srcProcessorFee ||
        fb?.dstProcessorFee ||
        this.transaction?.fee > 0
      );
    }
  },

  methods: {
    formatAmount(amount) {
      if (!amount) return '0';
      return parseInt(amount).toLocaleString('fr-FR');
    },

    getUsdtAmount(side) {
      const rawData = this.transaction?.rawData;
      if (!rawData) return null;

      const usdtData = rawData[side]?.data;
      if (usdtData?.usdtAmount) {
        return usdtData.usdtAmount;
      }
      return null;
    },

    copyReference() {
      Commons.copyToClipboard(
        this.transaction?.id,
        () => {
          this.$bus.emit(this.$constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
            title: 'référence copiée',
            body: this.transaction?.id
          });
        },
        () => {
          this.$bus.emit(this.$constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
            title: 'référence non copiée',
            body: 'veuillez réessayer'
          });
        }
      );
    }
  }
};
</script>
